import type { TFunction } from 'i18next';

import { mapScheduleOperationError } from 'src/sections/schedule/utils/schedule-operation-error';

import {
  ACTIVITY_DEPENDENCY_ERROR_CODE_TO_KEY,
  getActivityDependencyFeedbackKey,
} from './activity-dependency-errors';

/** Business error codes that can surface on activity create/update. */
export const ACTIVITY_SAVE_ERROR_CODE = {
  PROGRAMME_LOCKED: 'CFE000047',
  INVALID_DATA: 'CFE000018',
} as const;

const FEEDBACK_KEY_BY_CODE: Record<string, string> = {
  ...ACTIVITY_DEPENDENCY_ERROR_CODE_TO_KEY,
  [ACTIVITY_SAVE_ERROR_CODE.PROGRAMME_LOCKED]: 'PROGRAMME_LOCKED',
  [ACTIVITY_SAVE_ERROR_CODE.INVALID_DATA]: 'INVALID_DATA',
};

const CODE_BY_FEEDBACK_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(FEEDBACK_KEY_BY_CODE).map(([code, key]) => [key, code])
);

const GENERIC_API_MESSAGES = new Set([
  'Business Error',
  'GENERAL_ERROR',
  'INTERNAL_SERVER_ERROR',
  'VALIDATION_ERROR',
  'Something went wrong',
]);

type ApiErrorShape = {
  errorCode?: unknown;
  message?: unknown;
  key?: unknown;
  status?: unknown;
};

function isFeedbackKey(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Z][A-Z0-9_]+$/.test(value);
}

/** Resolves a `common:feedbackCodes.*` key for activity save failures, when known. */
export function getActivitySaveFeedbackKey(error: unknown): string | undefined {
  const dependencyKey = getActivityDependencyFeedbackKey(error);
  if (dependencyKey) {
    return dependencyKey;
  }

  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const apiError = error as ApiErrorShape;

  if (typeof apiError.errorCode === 'string' && FEEDBACK_KEY_BY_CODE[apiError.errorCode]) {
    return FEEDBACK_KEY_BY_CODE[apiError.errorCode];
  }

  if (isFeedbackKey(apiError.key) && CODE_BY_FEEDBACK_KEY[apiError.key]) {
    return apiError.key;
  }

  if (isFeedbackKey(apiError.message) && CODE_BY_FEEDBACK_KEY[apiError.message]) {
    return apiError.message;
  }

  return undefined;
}

function getDescriptiveApiMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const apiError = error as ApiErrorShape;
  if (typeof apiError.status === 'number' && apiError.status >= 500) {
    return undefined;
  }

  const message = apiError.message;
  if (typeof message !== 'string') {
    return undefined;
  }

  const trimmed = message.trim();
  if (!trimmed || GENERIC_API_MESSAGES.has(trimmed) || isFeedbackKey(trimmed)) {
    return undefined;
  }

  return trimmed;
}

/** Resolves a translated toast for activity create/update failures. */
export function getActivitySaveErrorMessage(
  error: unknown,
  t: TFunction,
  fallbackKey: string
): string {
  const scheduleFeedback = mapScheduleOperationError(error, { includeProjectRole: true });
  if (scheduleFeedback.kind === 'translated') {
    const scheduleMessage = t(scheduleFeedback.key, scheduleFeedback.values);
    if (scheduleMessage !== scheduleFeedback.key) {
      return scheduleMessage;
    }
  }

  const feedbackKey = getActivitySaveFeedbackKey(error);
  if (feedbackKey) {
    const translationKey = `common:feedbackCodes.${feedbackKey}`;
    const translation = t(translationKey);
    if (translation !== translationKey) {
      return translation;
    }
  }

  const apiMessage = getDescriptiveApiMessage(error);
  if (apiMessage) {
    return apiMessage;
  }

  return t(fallbackKey);
}
